import { createSlice, createAsyncThunk, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { oauth } from "../utils";
import { getFullItem } from "../selectors";
import { toast } from "react-toastify";
import { CatalogItem } from "../types";
import {
    CatalogItemsListMeta,
    EMPTY_CATALOG_ITEMS_LIST_META,
    parseCatalogItemsListMeta,
    mergeCatalogItemPages,
} from "../components/client-page/Catalog/catalogShared";
import {
    getConvertibleProducts,
    resetConvertibleProducts,
} from "./convertibleProducts";
import config from "../config";
import { ADD_ITEM_SUCCESS, HYDRATE_CATALOG_ITEM } from "../actions/item";

interface CatalogState {
    catalogId: string | null;
    publishedItems: CatalogItem[];
    unpublishedItems: CatalogItem[];
    publishedCount: number;
    unpublishedCount: number;
    publishedMeta: CatalogItemsListMeta;
    unpublishedMeta: CatalogItemsListMeta;
    hasEverHadPublishedItems: boolean;
    loading: "idle" | "pending";
    itemsLoading: "idle" | "pending";
    itemsLoadingMore: "idle" | "pending";
    itemsFetchGeneration: number;
    error: string | null;
    lookedUpClientId: string | null;
    csvExportLoading: "idle" | "pending";
    hydratedItemIds: Record<string, true>;
}

const initialState: CatalogState = {
    catalogId: null,
    publishedItems: [],
    unpublishedItems: [],
    publishedCount: 0,
    unpublishedCount: 0,
    publishedMeta: EMPTY_CATALOG_ITEMS_LIST_META,
    unpublishedMeta: EMPTY_CATALOG_ITEMS_LIST_META,
    hasEverHadPublishedItems: false,
    loading: "idle",
    itemsLoading: "idle",
    itemsLoadingMore: "idle",
    itemsFetchGeneration: 0,
    error: null,
    lookedUpClientId: null,
    csvExportLoading: "idle",
    hydratedItemIds: {},
};

type CatalogItemsQueryParams = {
    catalogId: string;
    search?: string;
    divisionId?: string | null;
    status?: string | null;
    is_upgradeable?: string | null;
    sort?: string | null;
    cursor?: string;
    silent?: boolean;
};

export type CatalogHydrationRootState = {
    catalog: CatalogState;
    entities: {
        items: Record<string, Record<string, unknown>>;
        orders: Record<string, { loaded?: boolean } & Record<string, unknown>>;
    };
};

function buildCatalogItemsQuery(
    params: CatalogItemsQueryParams,
    hidden: 0 | 1,
): Record<string, string | number> {
    const query: Record<string, string | number> = {
        page_limit: 32,
        hidden,
    };
    const search = params.search?.trim();
    if (search) {
        query.search = search;
    }
    if (params.divisionId) {
        query.division_id = params.divisionId;
    }
    if (hidden === 1) {
        if (params.status === "1") {
            query.status__gte = 1;
        } else if (params.status) {
            query.status = params.status;
        }
        if (params.is_upgradeable) {
            query.is_upgradeable = params.is_upgradeable;
        }
    }
    if (params.sort?.trim()) {
        query.sort = params.sort.trim();
    }
    if (params.cursor) {
        query.cursor = params.cursor;
    }
    return query;
}

// uses the minimal properties expected by getConvertibleProducts() and filters based on maybeBootstrapAndFetch()
function buildNonPSItemsForConvertible(items: CatalogItem[]) {
    return items
        .filter(
            (item) =>
                item.copied_from !== "ps-products" &&
                item.promo_api_name &&
                item.supplier_code &&
                !(
                    (config.blankSupplierPromoApiNames as string[]).includes(
                        item.promo_api_name,
                    ) && item.has_item_locations
                ),
        )
        .map((item) => ({
            promo_api_name: item.promo_api_name,
            sku: item.supplier_code,
            currency_id: item.currency_id,
            hasItemLocations: item.has_item_locations,
        }));
}

export const fetchClientCatalog = createAsyncThunk(
    "catalog/fetchClientCatalog",
    async (clientId: string) => {
        // GET /catalogs?client_id={clientId} — returns null catalogId when no catalog exists yet
        const { json } = await oauth("GET", "catalogs", {
            client_id: clientId,
            page_limit: 1,
        });
        const data = json?.data as Array<{ catalog_id: string }> | undefined;
        return {
            clientId,
            catalogId: data?.[0]?.catalog_id ?? null,
        };
    },
);

export const createClientCatalog = createAsyncThunk(
    "catalog/createClientCatalog",
    async (clientId: string) => {
        // POST /catalogs creates a new catalog or returns an existing one
        const { json } = await oauth("POST", "catalogs", {
            client_id: clientId,
        });
        return {
            clientId,
            catalogId: (json?.catalog_id as string) ?? null,
        };
    },
);

// fetches both tabs; updates item lists and tab counts
export const fetchCatalogItemsBoth = createAsyncThunk(
    "catalog/fetchCatalogItemsBoth",
    async (params: CatalogItemsQueryParams, { getState, dispatch }) => {
        // used to capture the current .pending fired so the reducer can detect and discard older responses
        const fetchGeneration = (getState() as { catalog: CatalogState }).catalog
            .itemsFetchGeneration;

        const [publishedResponse, unpublishedResponse] = await Promise.all([
            oauth(
                "GET",
                `catalogs/${params.catalogId}/items`,
                buildCatalogItemsQuery(params, 0),
            ),
            oauth(
                "GET",
                `catalogs/${params.catalogId}/items`,
                buildCatalogItemsQuery(params, 1),
            ),
        ]);

        const published = (publishedResponse.json?.data ?? []) as CatalogItem[];
        const unpublished = (unpublishedResponse.json?.data ?? []) as CatalogItem[];

        // only unpublished items require ePO status to be displayed
        const nonPSItems = buildNonPSItemsForConvertible(unpublished);
        if (nonPSItems.length > 0) {
            (dispatch as any)(getConvertibleProducts(nonPSItems));
        } else {
            (dispatch as any)(resetConvertibleProducts());
        }

        return {
            published,
            unpublished,
            publishedMeta: parseCatalogItemsListMeta(publishedResponse.json?.meta),
            unpublishedMeta: parseCatalogItemsListMeta(unpublishedResponse.json?.meta),
            fetchGeneration,
        };
    },
);

export const fetchCatalogItems = createAsyncThunk(
    "catalog/fetchCatalogItems",
    async (
        params: CatalogItemsQueryParams & { hidden: 0 | 1 },
        { getState, dispatch },
    ) => {
        const isCursor = !!params.cursor;
        const fetchGeneration = isCursor
            ? null
            : (getState() as { catalog: CatalogState }).catalog.itemsFetchGeneration;

        const query = buildCatalogItemsQuery(params, params.hidden);
        const { json } = await oauth("GET", `catalogs/${params.catalogId}/items`, query);
        const items = (json?.data ?? []) as CatalogItem[];

        // only unpublished items require ePO status to be displayed
        if (params.hidden === 1 && !isCursor) {
            const nonPSItems = buildNonPSItemsForConvertible(items);
            if (nonPSItems.length > 0) {
                await (dispatch as any)(getConvertibleProducts(nonPSItems));
            } else {
                (dispatch as any)(resetConvertibleProducts());
            }
        } else {
            (dispatch as any)(resetConvertibleProducts());
        }

        return {
            hidden: params.hidden,
            items,
            meta: parseCatalogItemsListMeta(json?.meta),
            fetchGeneration,
        };
    },
);

export const deleteCatalogItem = createAsyncThunk(
    "catalog/deleteCatalogItem",
    async ({ catalogId, itemId }: { catalogId: string; itemId: string }) => {
        await oauth(
            "DELETE",
            `catalogs/${catalogId}/items?item_id=${encodeURIComponent(itemId)}`,
        );
        return itemId;
    },
);

export const getItemDetails = createAsyncThunk(
    "catalog/getItemDetails",
    async (itemId: string) => {
        const { json } = await oauth("GET", `item/${itemId}`, {});
        const item = json?.item;
        if (!item) {
            return;
        }
        return item;
    },
);

type SetCatalogItemHiddenResult = { itemId: string; hidden: 0 | 1 };

const setCatalogItemPublished = async (
    itemId: string,
    hidden: 0 | 1,
    { rejectWithValue }: { rejectWithValue: (value: string) => unknown },
): Promise<SetCatalogItemHiddenResult> => {
    const failureMessage = hidden === 0
        ? "Failed to publish product"
        : "Failed to unpublish product";

    try {
        const result = await oauth("PUT", `item/${itemId}`, {
            hidden,
            field_lock: "unit_cost",
        });
        if (result?.response && !result.response.ok) {
            return rejectWithValue(failureMessage) as never;
        }
        return { itemId, hidden };
    } catch (err: unknown) {
        let error = (err as { json?: { error?: string | unknown[] } })?.json?.error;
        if (Array.isArray(error) && error.length > 0 && typeof error[0] === "string") {
            error = error[0];
        }
        return rejectWithValue(
            typeof error === "string" ? error : failureMessage,
        ) as never;
    }
};

export const publishCatalogItem = createAsyncThunk<
    SetCatalogItemHiddenResult,
    string,
    { rejectValue: string }
>(
    "catalog/publishCatalogItem",
    async (itemId, { rejectWithValue }) => {
        return await setCatalogItemPublished(itemId, 0, { rejectWithValue });
    },
);

export const unPublishCatalogItem = createAsyncThunk<
    SetCatalogItemHiddenResult,
    string,
    { rejectValue: string }
>(
    "catalog/unpublishCatalogItem",
    async (itemId, { rejectWithValue }) => {
        return await setCatalogItemPublished(itemId, 1, { rejectWithValue });
    },
);

export type CatalogCsvExportType = "all" | "published" | "unpublished";

export const exportCatalogCsv = createAsyncThunk<
    { blob: Blob; type: CatalogCsvExportType; catalogId: string },
    { catalogId: string; type: CatalogCsvExportType },
    { rejectValue: string }
>(
    "catalog/exportCatalogCsv",
    async ({ catalogId, type }, { rejectWithValue }) => {
        try {
            const { response } = await oauth(
                "POST",
                `catalogs/${catalogId}/generate-csv`,
                { type },
                null,
                { headers: { Accept: "text/csv" } },
            );

            if (!response.ok) {
                return rejectWithValue("Failed to export catalog") as never;
            }

            const blob = await response.blob();
            return { blob, type, catalogId };
        } catch {
            return rejectWithValue("Failed to export catalog") as never;
        }
    },
);

const catalogItemsSlice = createSlice({
    name: "catalogItems",
    initialState,
    reducers: {
        resetState: () => initialState,
        markCatalogItemHydrated(state, action: PayloadAction<string>) {
            state.hydratedItemIds[action.payload] = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClientCatalog.pending, (state) => {
                state.loading = "pending";
                state.error = null;
            })
            .addCase(fetchClientCatalog.fulfilled, (state, action) => {
                state.loading = "idle";
                state.lookedUpClientId = action.payload.clientId;
                state.catalogId = action.payload.catalogId;
            })
            .addCase(fetchClientCatalog.rejected, (state, action) => {
                state.loading = "idle";
                state.error = action.error?.message ?? "Failed to load catalog";
                toast.error(state.error);
            })
            .addCase(createClientCatalog.pending, (state) => {
                state.loading = "pending";
                state.error = null;
            })
            .addCase(createClientCatalog.fulfilled, (state, action) => {
                state.loading = "idle";
                state.lookedUpClientId = action.payload.clientId;
                state.catalogId = action.payload.catalogId;
            })
            .addCase(createClientCatalog.rejected, (state, action) => {
                state.loading = "idle";
                state.error = action.error?.message ?? "Failed to create catalog";
                toast.error(state.error);
            })
            .addCase(fetchCatalogItemsBoth.pending, (state) => {
                state.itemsFetchGeneration += 1;
                state.itemsLoading = "pending";
                state.error = null;
            })
            .addCase(fetchCatalogItemsBoth.fulfilled, (state, action) => {
                if (action.payload.fetchGeneration !== state.itemsFetchGeneration) {
                    return;
                }
                state.itemsLoading = "idle";
                state.publishedItems = action.payload.published;
                state.unpublishedItems = action.payload.unpublished;
                state.publishedCount = action.payload.published.length;
                state.unpublishedCount = action.payload.unpublished.length;
                state.publishedMeta = action.payload.publishedMeta;
                state.unpublishedMeta = action.payload.unpublishedMeta;
                if (state.publishedItems.length > 0) {
                    state.hasEverHadPublishedItems = true;
                }
            })
            .addCase(fetchCatalogItemsBoth.rejected, (state, action) => {
                state.itemsLoading = "idle";
                state.error = action.error?.message ?? "Failed to load catalog items";
                toast.error(state.error);
            })
            .addCase(fetchCatalogItems.pending, (state, action) => {
                if (action.meta.arg.cursor) {
                    state.itemsLoadingMore = "pending";
                } else if (!action.meta.arg.silent) {
                    state.itemsFetchGeneration += 1;
                    state.itemsLoading = "pending";
                }
                state.error = null;
            })
            .addCase(fetchCatalogItems.fulfilled, (state, action) => {
                const { hidden, items, meta, fetchGeneration } = action.payload;
                const isCursor = !!action.meta.arg.cursor;

                // Discard stale non-cursor responses
                if (!isCursor && fetchGeneration !== state.itemsFetchGeneration) {
                    return;
                }

                if (isCursor) {
                    state.itemsLoadingMore = "idle";
                } else {
                    state.itemsLoading = "idle";
                }

                if (hidden === 1) {
                    state.unpublishedItems = mergeCatalogItemPages(
                        state.unpublishedItems,
                        items,
                        !isCursor,
                    );
                    state.unpublishedCount = state.unpublishedItems.length;
                    state.unpublishedMeta = meta;
                } else {
                    state.publishedItems = mergeCatalogItemPages(
                        state.publishedItems,
                        items,
                        !isCursor,
                    );
                    state.publishedCount = state.publishedItems.length;
                    state.publishedMeta = meta;
                    if (state.publishedItems.length > 0) {
                        state.hasEverHadPublishedItems = true;
                    }
                }
            })
            .addCase(fetchCatalogItems.rejected, (state, action) => {
                if (action.meta.arg.cursor) {
                    state.itemsLoadingMore = "idle";
                } else {
                    state.itemsLoading = "idle";
                }
                state.error = action.error?.message ?? "Failed to load catalog items";
                toast.error(state.error);
            })
            .addCase(deleteCatalogItem.pending, (state) => {
                state.itemsLoading = "pending";
                state.error = null;
            })
            .addCase(deleteCatalogItem.fulfilled, (state, action) => {
                state.itemsLoading = "idle";
                const itemId = action.payload;
                state.unpublishedItems = state.unpublishedItems.filter((i) => i.item_id !== itemId);
                state.unpublishedCount = Math.max(0, state.unpublishedCount - 1);
                delete state.hydratedItemIds[itemId];
                toast.success("Product deleted successfully");
            })
            .addCase(ADD_ITEM_SUCCESS, (state, action) => {
                const payload = (action as PayloadAction<{
                    order?: { order_id?: string };
                    item?: { item_id?: string };
                }>).payload;
                const orderId = payload.order?.order_id;
                const itemId = payload.item?.item_id;
                if (orderId && itemId && orderId === state.catalogId) {
                    state.hydratedItemIds[itemId] = true;
                }
            })
            .addCase(HYDRATE_CATALOG_ITEM, (state, action) => {
                const payload = (action as PayloadAction<{
                    order?: { order_id?: string };
                    item?: { item_id?: string };
                }>).payload;
                const orderId = payload.order?.order_id;
                const itemId = payload.item?.item_id;
                if (orderId && itemId && orderId === state.catalogId) {
                    state.hydratedItemIds[itemId] = true;
                }
            })
            .addCase(deleteCatalogItem.rejected, (state, action) => {
                state.itemsLoading = "idle";
                state.error = action.error?.message ?? "Failed to delete product";
                toast.error(state.error);
            })
            .addCase(getItemDetails.rejected, (state, action) => {
                state.error = action.error?.message ?? "Failed to get item details";
                toast.error(state.error);
            })
            .addCase(publishCatalogItem.pending, (state) => {
                state.error = null;
            })
            .addCase(publishCatalogItem.fulfilled, (state, action) => {
                const { itemId } = action.payload;
                const item = state.unpublishedItems.find((i) => i.item_id === itemId);
                state.unpublishedItems = state.unpublishedItems.filter((i) => i.item_id !== itemId);
                state.unpublishedCount = Math.max(0, state.unpublishedCount - 1);
                if (item) {
                    state.publishedItems.push({ ...item, hidden: 0 });
                    state.publishedCount += 1;
                    state.hasEverHadPublishedItems = true;
                }
            })
            .addCase(publishCatalogItem.rejected, (state, action) => {
                const message =
                    (typeof action.payload === "string" ? action.payload : null)
                    ?? action.error?.message
                    ?? "Failed to publish product";
                state.error = message;
                toast.error(message);
            })
            .addCase(unPublishCatalogItem.pending, (state) => {
                state.error = null;
            })
            .addCase(unPublishCatalogItem.fulfilled, (state, action) => {
                const { itemId } = action.payload;
                const item = state.publishedItems.find((i) => i.item_id === itemId);
                state.publishedItems = state.publishedItems.filter((i) => i.item_id !== itemId);
                state.publishedCount = Math.max(0, state.publishedCount - 1);
                if (item) {
                    state.unpublishedItems.push({ ...item, hidden: 1 });
                    state.unpublishedCount += 1;
                }
            })
            .addCase(unPublishCatalogItem.rejected, (state, action) => {
                const message =
                    (typeof action.payload === "string" ? action.payload : null)
                    ?? action.error?.message
                    ?? "Failed to unpublish product";
                state.error = message;
                toast.error(message);
            })
            .addCase(exportCatalogCsv.pending, (state) => {
                state.csvExportLoading = "pending";
                state.error = null;
            })
            .addCase(exportCatalogCsv.fulfilled, (state) => {
                state.csvExportLoading = "idle";
            })
            .addCase(exportCatalogCsv.rejected, (state, action) => {
                state.csvExportLoading = "idle";
                const message =
                    (typeof action.payload === "string" ? action.payload : null)
                    ?? action.error?.message
                    ?? "Failed to export catalog";
                state.error = message;
                toast.error(message);
            });
    },
});

export const { resetState, markCatalogItemHydrated } = catalogItemsSlice.actions;

export const selectCatalogId = (state: { catalog: CatalogState }) =>
    state.catalog.catalogId;

export const selectPublishedCatalogItems = (state: { catalog: CatalogState }) =>
    state.catalog.publishedItems;

export const selectUnpublishedCatalogItems = (state: { catalog: CatalogState }) =>
    state.catalog.unpublishedItems;

export const selectPublishedCatalogItemsCount = (state: { catalog: CatalogState }) =>
    state.catalog.publishedCount;

export const selectUnpublishedCatalogItemsCount = (state: { catalog: CatalogState }) =>
    state.catalog.unpublishedCount;

export const selectPublishedCatalogItemsHasMore = (state: { catalog: CatalogState }) =>
    state.catalog.publishedMeta.hasMore;

export const selectUnpublishedCatalogItemsHasMore = (state: { catalog: CatalogState }) =>
    state.catalog.unpublishedMeta.hasMore;

export const selectPublishedCatalogItemsCursor = (state: { catalog: CatalogState }) =>
    state.catalog.publishedMeta.cursor;

export const selectUnpublishedCatalogItemsCursor = (state: { catalog: CatalogState }) =>
    state.catalog.unpublishedMeta.cursor;

export const selectCatalogItemsLoadingMore = (state: { catalog: CatalogState }) =>
    state.catalog.itemsLoadingMore === "pending";

export const selectCatalogLoading = (state: { catalog: CatalogState }) =>
    state.catalog.loading === "pending" || state.catalog.itemsLoading === "pending";

export const selectCatalogItemsLoading = (state: { catalog: CatalogState }) =>
    state.catalog.itemsLoading === "pending";

export const selectCatalogError = (state: { catalog: CatalogState }) =>
    state.catalog.error;

export const selectLookedUpClientId = (state: { catalog: CatalogState }) =>
    state.catalog.lookedUpClientId;

export const selectCatalogCsvExportLoading = (state: { catalog: CatalogState }) =>
    state.catalog.csvExportLoading === "pending";

export const selectHasEverHadPublishedItems = (state: { catalog: CatalogState }) =>
    state.catalog.hasEverHadPublishedItems;

export const selectIsCatalogItemHydrated = (
    state: CatalogHydrationRootState,
    itemId: string,
) => state.catalog.hydratedItemIds[itemId] === true;

export const selectCatalogOrder = (
    state: CatalogHydrationRootState,
    catalogId: string,
    itemId: string,
) => {
    const entity = state.entities.orders[catalogId];
    const hydrated = selectIsCatalogItemHydrated(state, itemId);
    return {
        ...(entity ?? {}),
        order_id: catalogId,
        loaded: !!entity?.loaded || hydrated,
    };
};

export const getCatalogFullItem = createSelector(
    (state: CatalogHydrationRootState, props: { item_id: string }) =>
        getFullItem(state, { item_id: props.item_id }),
    (item) => {
        if (!item) {
            return null;
        }

        const artworks = Array.isArray(item.artworks) ? item.artworks : [];
        const itemLocations = Array.isArray(item.item_locations) ? item.item_locations : [];

        return {
            ...item,
            item_locations: itemLocations.map((location: Record<string, unknown>) => {
                const locationId = location.item_location_id as string | undefined;
                return {
                    ...location,
                    item_location_title:
                        typeof location.item_location_title === "string"
                            ? location.item_location_title
                            : "",
                    artworks: artworks.filter(
                        (artwork: Record<string, unknown>) =>
                            artwork.item_location_id === locationId,
                    ),
                };
            }),
        };
    },
);

export default catalogItemsSlice.reducer;
